import { DRIVER_LEDGER_KIND_LABELS, DRIVER_LEDGER_KINDS, type DriverLedgerEntryView } from '@chukua/common';
import { Table, type TableColumnsType } from 'antd';
import { BookOpen } from 'lucide-react';
import { EmptyState } from '../../../shared/components/EmptyState';
import type { useDriverLedger } from '../hooks/use-driver-ledger';
import { entryEffectLabel, formatLedgerAmount } from '../policies/ledger-amount-labels';

type LedgerList = ReturnType<typeof useDriverLedger>['list'];

function ledgerColumns(list: LedgerList): TableColumnsType<DriverLedgerEntryView> {
  return [
    {
      title: 'When',
      key: 'createdAt',
      sorter: true,
      sortOrder: list.sortOrderFor('createdAt'),
      render: (_, entry) => new Date(entry.createdAt).toLocaleString(),
    },
    {
      title: 'Kind',
      key: 'kind',
      sorter: true,
      sortOrder: list.sortOrderFor('kind'),
      filters: DRIVER_LEDGER_KINDS.map((kind) => ({ text: DRIVER_LEDGER_KIND_LABELS[kind], value: kind })),
      filteredValue: list.filteredValueFor('kind'),
      render: (_, entry) => DRIVER_LEDGER_KIND_LABELS[entry.kind],
    },
    {
      title: 'Amount',
      key: 'deltaMinor',
      sorter: true,
      sortOrder: list.sortOrderFor('deltaMinor'),
      render: (_, entry) => (
        <span>
          {formatLedgerAmount(entry.deltaMinor, entry.currency)} <span className="text-muted">· {entryEffectLabel(entry.deltaMinor)}</span>
        </span>
      ),
    },
    { title: 'Note', key: 'note', render: (_, entry) => entry.note ?? <span className="text-muted">—</span> },
    { title: 'Recorded by', key: 'recordedBy', render: (_, entry) => entry.recordedBy?.name ?? <span className="text-muted">System</span> },
  ];
}

interface DriverLedgerTableProps {
  items: DriverLedgerEntryView[];
  total: number;
  list: LedgerList;
}

/** The ledger rows with the server-side sort and kind filter. */
export function DriverLedgerTable({ items, total, list }: DriverLedgerTableProps) {
  return (
    <Table
      rowKey="id"
      dataSource={items}
      columns={ledgerColumns(list)}
      locale={{ emptyText: <EmptyState icon={BookOpen} title="No entries match" description="Clear the filters to see every entry." /> }}
      scroll={{ x: 'max-content' }}
      {...list.tableProps<DriverLedgerEntryView>(total)}
    />
  );
}
